import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Box, Paper, makeStyles } from "@material-ui/core";

import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import AppConfirmDialog from "components/Dialog/AppConfirmDialog";
import { deleteClient, getClientActiveTab, setClientActiveTab } from "store/slices/client";
import { getAuthInfo } from "store/slices/auth";
import TableImportClient from "./Component/Client/table/TableImportClient";
import TableInformation from "./Component/Client/table/TableInformation";
import SendIntakeLinkModal from "./Component/Client/SendIntakeLinkModal";

const useStyles = makeStyles(() => ({
  container: {
    padding: "1rem",
  },
  card: {
    background: "#fff",
    borderRadius: 8,
    border: "1px solid #E5E7EB",
    padding: 0,
    overflow: "hidden",
  },
  tabsContainer: {
    display: "flex",
    borderBottom: "1px solid #E5E7EB",
    flexWrap: "wrap",
  },
  tab: {
    padding: "9px 16px",
    fontSize: 12,
    color: "#6B7280",
    cursor: "pointer",
    borderBottom: "2.5px solid transparent",
    marginBottom: -1,
    whiteSpace: "nowrap",
    "&:hover": {
      color: "#111827",
    },
  },
  tabActive: {
    color: "#0D9488",
    borderBottomColor: AppColor.mainColor,
    fontWeight: 600,
  },
  tabContent: {
    padding: 16,
  },
}));

const ClientManagement = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const authInfo = useSelector(getAuthInfo);
  const savedActiveTab = useSelector(getClientActiveTab);

  const [selected, setSelected] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [activeTab, setActiveTabState] = useState(savedActiveTab);
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const setActiveTab = (index) => {
    setActiveTabState(index);
    dispatch(setClientActiveTab(index));
  };

  const tableInfoRef = useRef();

  const _handleDeleteClients = async () => {
    if (selected.length > 0) {
      setIsDeleting(true);
      try {
        const params = { ids: selected, companyId: authInfo.companyId };
        await dispatch(deleteClient(params));
        setShowDeleteModal(false);

        if (tableInfoRef.current) {
          const currentQueries = tableInfoRef.current.getCurrentQueries();
          const dataLength = tableInfoRef.current.getDataLength();
          if (
            dataLength === 1 ||
            (selected.length === dataLength && currentQueries.page > 1)
          ) {
            tableInfoRef.current.loadWithQueries({
              ...currentQueries,
              page: currentQueries.page - 1,
            });
          } else {
            tableInfoRef.current.refreshData();
          }
        }
        setSelected([]);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const _handleCreate = () => {
    if (!authInfo.accountingType) {
      setShowConfirmModal(true);
    } else {
      history.push("/admin/participants/create");
    }
  };

  const _handleSendIntakeLink = () => {
    setShowIntakeModal(true);
  };

  const _handleGoToSettingAccounting = () => {
    history.push("/admin/setting-accounting");
  };

  const tabs = [
    {
      label: t("label.participants"),
      component: (
        <TableInformation
          ref={tableInfoRef}
          selected={selected}
          setSelected={setSelected}
        />
      ),
    },
    {
      label: t("label.importParticipants"),
      component: <TableImportClient />,
    },
  ];

  return (
    <Box className={classes.container}>
      <Box mb={2} display="flex" alignItems="center">
        <AppButton
          label="+ Create"
          onClick={_handleCreate}
          buttonColor="#2dd4bf"
          style={{ marginRight: "1rem", textTransform: "none" }}
        />
        <AppButton
          label={t("button.delete")}
          buttonColor={AppColor.pink}
          onClick={() => setShowDeleteModal(true)}
          disabled={selected.length === 0 || isDeleting}
          isLoading={isDeleting}
          style={{ marginRight: "1rem", textTransform: "none" }}
        />
        {/* <AppButton
          label={t("label.send_intake_link")}
          mode="outlined"
          buttonColor="#2dd4bf"
          startIcon={<i className="fas fa-paper-plane" />}
          style={{ textTransform: "none", backgroundColor: "white" }}
        /> */}
        <AppButton
          onClick={_handleSendIntakeLink}
          mode="outlined"
          style={{ textTransform: "none", backgroundColor: "#fff", borderWidth: "1px" }}
        >
          <i className="fas fa-paper-plane mr-2" />
          {t("label.send_intake_link")}
        </AppButton>
      </Box>

      <Paper className={classes.card}>
        <Box className={classes.tabsContainer}>
          {tabs.map((tab, index) => (
            <Box
              key={index}
              className={`${classes.tab} ${
                activeTab === index ? classes.tabActive : ""
              }`}
              onClick={() => setActiveTab(index)}
            >
              {tab.label}
            </Box>
          ))}
        </Box>
        <Box className={classes.tabContent}>{tabs[activeTab].component}</Box>
      </Paper>

      <AppConfirmDialog
        open={showDeleteModal}
        onConfirm={_handleDeleteClients}
        onClose={() => setShowDeleteModal(false)}
        title={t("title.delete_participants")}
        isDisabledButton={isDeleting}
        content={t("text.delete_participants")}
      />
      <AppConfirmDialog
        open={showConfirmModal}
        onConfirm={_handleGoToSettingAccounting}
        onClose={() => setShowConfirmModal(false)}
        title={t("title.check_accounting_type")}
        content={t("text.can_not_have_accounting")}
      />
      
      <SendIntakeLinkModal
        open={showIntakeModal}
        onClose={() => setShowIntakeModal(false)}
        onSuccess={() => {
          setShowIntakeModal(false);
          if (tableInfoRef.current) {
            tableInfoRef.current.refreshData();
          }
        }}
      />
    </Box>
  );
};

export default ClientManagement;
